// ── ADMIN: BIRTHDAYS ─────────────────────────────────────────────────
// Pilot domain for the ES-module migration, and again for the onclick →
// addEventListener migration (phase 2 of closing the onclick/window gap —
// see tools/check-onclick-handlers.mjs for phase 1). Note: getBirthdayWindow
// through previewBirthdayEmail were physically located in a separate
// "BIRTHDAY LOGIC" section far away in the original file (near the datetime
// picker), not next to VIEWS['a-birthdays'] itself — they're gathered here
// since they're this view's actual logic. birthdayStar() was physically
// embedded inside the unrelated Registrations Queue section; it's also
// genuinely birthdays-only (a gold star badge). birthdayStar and
// upcomingBirthdaysForProgram are real ES exports, imported by
// admin-dashboard.js. saveBirthdayRecip/previewBirthdayEmail are wired via
// VIEW_INIT below instead of window/onclick — direct addEventListener
// references, re-attached on every re-render since #main's innerHTML is
// replaced each route().
import { apiFetch } from '../core/api.js';
import { DB, PROGRAMS } from '../core/state.js';
import { TODAY } from '../core/format.js';
import { activePrograms, programName, kidInProgram, isValidEmail, isArchived } from '../core/domain-utils.js';
import { openModal, toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, sectionTabs } from '../core/routing.js';

export function birthdayStar() {
  return `<span style="flex-shrink:0;width:24px;height:24px;border-radius:50%;background:rgba(201,168,76,0.1);border:1px solid var(--border);display:inline-flex;align-items:center;justify-content:center">
    <svg aria-hidden="true" viewBox="0 0 16 16" width="12" height="12" fill="var(--gold)" stroke="none"><path d="M8 1.5l1.85 3.95 4.15.55-3.05 2.9.78 4.1L8 11.9 4.27 13l.78-4.1L2 6l4.15-.55z"/></svg>
  </span>`;
}

// The digest week runs Wednesday through Tuesday, and cron/birthday_digest.php
// sends it on Wednesday morning covering today + 6 days. So the window has to
// be anchored to the Wednesday that has already happened (today, if today is
// Wednesday), not the next one -- anchoring forward meant that on Wednesday the
// portal showed the *following* week's birthdays while the digest for this week
// was going out, and the current week only ever appeared once it was over.
// Birthdays earlier in the current week drop out on their own: the bday < TODAY
// rollover below pushes them to next year, out of the window.
function getBirthdayWindow() {
  const daysSinceWed = (TODAY.getDay() - 3 + 7) % 7;
  const windowStart = new Date(TODAY); windowStart.setDate(TODAY.getDate() - daysSinceWed);
  const windowEnd   = new Date(windowStart); windowEnd.setDate(windowStart.getDate() + 6);
  return { windowStart, windowEnd };
}
export function upcomingBirthdaysForProgram(programId) {
  const { windowStart, windowEnd } = getBirthdayWindow();
  const DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const results = [];
  // Archived performers are excluded here to match cron/birthday_digest.php --
  // the on-screen list and the digest Lori's staff receive have to agree, or
  // one of them is lying about who's celebrating this week.
  DB.children.filter(k => k.dob && !isArchived(k) && kidInProgram(k, programId)).forEach(k => {
    const dob  = new Date(k.dob + 'T00:00');
    const dobMonth = dob.getMonth() + 1;
    // Off-season birthdays (May–Aug) are celebrated on their half birthday
    const isHalf = dobMonth >= 5 && dobMonth <= 8;
    const celebDate = isHalf ? new Date(dob.getFullYear(), dob.getMonth() + 6, dob.getDate()) : dob;
    const bday = new Date(TODAY.getFullYear(), celebDate.getMonth(), celebDate.getDate());
    if (bday < TODAY) bday.setFullYear(TODAY.getFullYear() + 1);
    if (bday >= windowStart && bday <= windowEnd) {
      const turningAge = bday.getFullYear() - dob.getFullYear() - (isHalf ? 1 : 0);
      results.push({ name: k.name, bday, turningAge, dayName: DAYS[bday.getDay()], dateStr: MONTHS[bday.getMonth()] + ' ' + bday.getDate(), isHalf });
    }
  });
  return results.sort((a,b) => a.bday - b.bday);
}
function upcomingBirthdaysByProgram() {
  const result = {};
  activePrograms().filter(p => p.isProgram).forEach(p => { result[p.id] = upcomingBirthdaysForProgram(p.id); });
  return result;
}
// In season the digest goes out every week, birthdays or not, so a quiet week is
// visibly a quiet week rather than a broken cron. Mirrors cron/birthday_digest.php:
// in season means the window's month is in the program's seasonMonths (the same
// list that drives monthly billing), and a year-round program has none set.
function programInSeason(programId) {
  const p = PROGRAMS.find(p => p.id === programId);
  if (!p || !Array.isArray(p.seasonMonths)) return true;
  return p.seasonMonths.includes(getBirthdayWindow().windowStart.getMonth() + 1);
}
function previewBirthdayEmail(programId) {
  const upcoming = upcomingBirthdaysForProgram(programId);
  const pName    = programName(programId);
  const recip    = DB.settings.birthdayRecipients[programId] || '(no recipients set)';
  // No birthdays this week: still sent in season, so recipients can tell a quiet
  // week from a missing email. Out of season, nothing goes out.
  if (!upcoming.length && !programInSeason(programId)) {
    openModal(`
      <h3>Birthday digest preview — ${esc(pName)}</h3>
      <div style="background:rgba(255,255,255,0.03);border:1px solid var(--border-soft);border-radius:8px;padding:20px;text-align:center">
        <p style="font-size:14px;color:var(--cream);margin-bottom:6px">No birthdays in ${esc(pName)} this week.</p>
        <p style="font-size:13px;color:var(--muted)">No digest will be sent — ${esc(pName)} is out of season this week, and out-of-season weeks with no birthdays are skipped.</p>
      </div>`);
    return;
  }
  const lines = upcoming.length
    ? upcoming.map(b => `★ ${b.name} — ${b.dayName}, ${b.dateStr}${b.isHalf ? ' (½ birthday!)' : ' (turning ' + b.turningAge + ')'}`).join('\n')
    : '';
  openModal(`
    <h3>Birthday digest preview — ${esc(pName)}</h3>
    <p style="font-size:12px;color:var(--muted);margin-bottom:14px">To: ${esc(recip)}</p>
    <div style="background:rgba(255,255,255,0.03);border:1px solid var(--border-soft);border-radius:8px;padding:16px">
      <div style="font-size:12px;color:var(--muted);margin-bottom:8px">Subject: <span style="color:var(--cream)">CSYP: ${upcoming.length ? '' : 'no '}${esc(pName)} birthdays this week</span></div>
      <div style="font-size:14px;color:var(--cream);white-space:pre-line;line-height:1.9">Hi team,

${upcoming.length
  ? `Here are the ${esc(pName)} performers with birthdays coming up this Wednesday through Tuesday — give them a shoutout at rehearsal!`
  : `No ${esc(pName)} performers have a birthday this Wednesday through Tuesday. This note goes out every week during the season, so a quiet week looks different from a missing email.`}

${lines}

Have a great week,
Center Stage Youth Performers</div>
    </div>`);
}

VIEWS['a-birthdays'] = () => {
  const s = DB.settings;
  const bdayByProgram = upcomingBirthdaysByProgram();
  const mainPrograms = activePrograms().filter(p => p.isProgram);
  return `
    <div class="page-head">
      <h2 class="serif">Birthday <em>Emails</em></h2>
    </div>
    ${sectionTabs('a-settings')}
    <p style="font-size:13px;color:var(--muted);margin-bottom:24px;max-width:640px">A weekly digest of upcoming performer birthdays, sent to the recipients you choose for each program.</p>
    <div class="grid-2">
      ${mainPrograms.map(p => {
        const recip = s.birthdayRecipients[p.id] || '';
        const upcoming = bdayByProgram[p.id] || [];
        return `
        <div class="card">
          <div class="card-hd">${esc(p.name)}</div>
          <p style="font-size:13px;color:var(--muted);margin-bottom:12px">Weekly digest covering performers in <b style="color:var(--cream)">${esc(p.name)}</b> only.</p>
          <div style="display:flex;gap:8px;align-items:flex-end;margin-bottom:16px">
            <div class="field" style="flex:1;margin-bottom:0"><label>Recipients</label><input type="text" id="bdayRecip_${p.id}" value="${esc(recip)}" placeholder="email@csyp.org"></div>
            <button class="btn-ghost btn-sm" style="flex-shrink:0;margin-bottom:1px" data-action="save-recip" data-program="${p.id}">Save</button>
          </div>
          <div style="font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:var(--muted);margin-bottom:10px">Upcoming birthdays this week</div>
          ${upcoming.length ? upcoming.map(b => `
            <div style="display:flex;align-items:center;gap:10px;padding:7px 0;border-bottom:1px solid var(--border-soft)">
              ${birthdayStar()}
              <span style="flex:1;min-width:0;font-size:13px;color:var(--cream)">${esc(b.name)}${b.isHalf ? ' <span style="font-size:11px;color:var(--gold-light)">½ birthday</span>' : ''}</span>
              <span style="font-size:12px;color:var(--muted);flex-shrink:0">${b.dayName.slice(0,3)} ${b.dateStr}${b.isHalf ? '' : ' · turning ' + b.turningAge}</span>
            </div>`).join('') : `<p class="empty" style="padding:6px 0 0">No birthdays in this window.${programInSeason(p.id) ? ' The digest still goes out this week saying so.' : ' ' + esc(p.name) + ' is out of season, so no digest is sent.'}</p>`}
          <button class="btn-ghost btn-sm" data-action="preview-digest" data-program="${p.id}" style="margin-top:14px">Preview digest</button>
        </div>`;
      }).join('')}
    </div>`;
};
async function saveBirthdayRecip(programId) {
  const val = document.getElementById('bdayRecip_' + programId).value.trim();
  const emails = val.split(',').map(e => e.trim()).filter(Boolean);
  const bad = emails.filter(e => !isValidEmail(e));
  if (bad.length) { toast('Please check the email address' + (bad.length > 1 ? 'es' : '') + ': ' + bad.join(', ')); return; }
  try {
    const recipients = { ...DB.settings.birthdayRecipients, [programId]: val };
    await apiFetch('/settings/birthday-recipients', { method: 'PUT', body: JSON.stringify({ recipients }) });
    DB.settings.birthdayRecipients = recipients;
    toast('Recipients saved for ' + programName(programId));
  } catch (e) { toastError(e.message); }
}

VIEW_INIT['a-birthdays'] = () => {
  document.querySelectorAll('[data-action="save-recip"]').forEach(btn => {
    btn.addEventListener('click', () => saveBirthdayRecip(btn.dataset.program));
  });
  document.querySelectorAll('[data-action="preview-digest"]').forEach(btn => {
    btn.addEventListener('click', () => previewBirthdayEmail(btn.dataset.program));
  });
};
